/**
 * THE single number-rendering function.
 *
 * No figure is ever typed as a literal into a template. `npm run lint:literals`
 * fails the build if one is, which is what turns "every number is traceable"
 * from an intention into a property of the build.
 */

import type { MetricResult, Range, UnitId, Undisclosed } from '../engine/schema.js';
import { isUndisclosed } from '../engine/schema.js';
import { crossesZero, isPoint } from '../engine/range.js';
import { formatValue, isSelfLabelling, roundToSigFigs, unitAbbr } from '../engine/units.js';

export function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface NumOpts {
  /** Renders as a button that opens the provenance drawer. */
  drawerId?: string;
  /** Suppress the range line (e.g. inside a dense table). */
  compact?: boolean;
  /** Override the unit label shown beneath the figure. */
  unitLabel?: string;
}

/** Value + range as plain text. Used in charts, tables and copy-to-clipboard. */
export function formatRange(r: Range, unit: UnitId, sigFigs: number): string {
  const mid = formatValue(roundToSigFigs(r.mid, sigFigs), unit);
  if (isPoint(r)) return mid;
  const lo = formatValue(roundToSigFigs(r.lo, sigFigs), unit);
  const hi = formatValue(roundToSigFigs(r.hi, sigFigs), unit);
  return `${mid} (range ${lo} to ${hi})`;
}

export function formatMid(r: Range, unit: UnitId, sigFigs: number): string {
  const rounded = roundToSigFigs(r.mid, sigFigs);
  // An approximation sign wherever there is a band. A bare figure off a wide
  // range reads as precision the number does not have.
  return `${isPoint(r) ? '' : '≈'}${formatValue(rounded, unit)}`;
}

/**
 * Render a metric result as the standard figure block.
 * Blocked results render as "not publicly disclosed", never as zero.
 */
export function num(result: MetricResult, opts: NumOpts = {}): string {
  if (result.blocked || isUndisclosed(result.value)) {
    return undisclosedBlock(result.value as Undisclosed);
  }

  const r = result.value;
  const { unit, sigFigs } = result;

  // A figure like "55.5" is not an answer to "how much water" unless the unit
  // is on screen. Currency and percentages carry their own symbol; everything
  // else gets its unit appended unless the caller has named one already.
  const unitLabel = opts.unitLabel ?? (isSelfLabelling(unit) ? '' : unitAbbr(unit));

  const inner = [
    `<span class="figure-v">${esc(formatMid(r, unit, sigFigs))}</span>`,
    unitLabel ? `<span class="figure-u">${esc(unitLabel)}</span>` : '',
    !opts.compact && !isPoint(r)
      ? `<span class="figure-range">range ${esc(
          formatValue(roundToSigFigs(r.lo, sigFigs), unit),
        )} to ${esc(formatValue(roundToSigFigs(r.hi, sigFigs), unit))}${
          crossesZero(r) ? ' — could be either a cost or a benefit' : ''
        }</span>`
      : '',
    opts.drawerId
      ? `<span class="figure-hint">Show the work</span>`
      : '',
  ].join('');

  if (!opts.drawerId) return `<div class="figure">${inner}</div>`;

  return `<button class="figure" data-drawer="${esc(opts.drawerId)}" aria-haspopup="dialog">${inner}</button>`;
}

function undisclosedBlock(u: Undisclosed): string {
  return `<p class="figure-blocked">Not publicly disclosed. ${esc(u.note)}${
    u.requestedFrom ? ` Requested from ${esc(u.requestedFrom)}.` : ''
  }</p>`;
}

/** A single scalar with its unit, for tables and inline use. */
export function scalar(v: number, unit: UnitId, sigFigs = 3): string {
  return formatValue(roundToSigFigs(v, sigFigs), unit);
}

export function confidenceChip(c: string): string {
  return `<span class="confidence-chip ${c === 'placeholder' ? 'placeholder' : ''}">${esc(c)}</span>`;
}
